import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Sparkles, Settings, Wand2, Upload, X, RefreshCw, ImagePlus, Maximize2, Star, Pin, Sun, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import useGreenScreen from "../lib/useGreenScreen";
import { drawCover } from "../lib/canvasUtils";
/**
 * MobileGreenScreenBar — Instagram/TikTok-style inline Green Screen UX
 * for the Mobile Live Studio.
 *
 * Replaces the bottom-sheet `GreenScreenStudio` on phones. Lives as a
 * compact strip inside `MobileLiveStudio`'s lower toolbar with:
 *
 *   • A single ON / OFF tap (the strip header).
 *   • A horizontal swipe carousel of background thumbnails — tap to
 *     apply, long-press to favourite.
 *   • A NONE chip + a BLUR chip prepended so "soft Zoom blur" is one
 *     tap away regardless of the library state.
 *   • Quick toggle between AI Person Cutout and Green Wall chroma key.
 *   • Gear icon → Advanced sheet (upload, AI generate, fine-tune).
 *
 * Persists user prefs (mode, enabled, last background URL) to the same
 * localStorage key the desktop GreenScreenStudio uses so the experience
 * carries across devices for the same logged-in user.
 *
 * Props:
 *   cameraStream         current MediaStream from the broadcaster (must
 *                        have a video track) — the compositor source.
 *   onCompositedTrack    callback(track | null) — fires whenever the
 *                        produced composite track changes (e.g. user
 *                        toggles off, in which case track === null and
 *                        the parent must restore the raw camera track).
 *   onOpenAdvanced       optional — when present, the gear icon shows
 *                        and clicking it opens the parent's advanced
 *                        sheet. We don't own that sheet to avoid
 *                        double-mounting GS UIs.
 */
const STORAGE_KEY = "tapin_greenscreen_settings_v3"; // matches GreenScreenStudio

function readPrefs() {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    return raw && typeof raw === "object" ? raw : {};
  } catch {
    return {};
  }
}
function writePrefs(p) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(p)); } catch { /* quota */ }
}

const RECENTS_KEY = "tapin_gs_recent_bgs_v1";
function readRecents() {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENTS_KEY) || "null");
    return Array.isArray(raw) ? raw.slice(0, 12) : [];
  } catch { return []; }
}
function pushRecent(item) {
  if (!item?.id) return;
  const cur = readRecents();
  const filtered = cur.filter((x) => x.id !== item.id);
  const next = [{ ...item, _ts: Date.now() }, ...filtered].slice(0, 12);
  try { localStorage.setItem(RECENTS_KEY, JSON.stringify(next)); } catch { /* quota */ }
}

export default function MobileGreenScreenBar({
  cameraStream,
  onCompositedTrack,
  onOpenAdvanced,
  captureFps = 30,
}) {
  const initial = readPrefs();
  const [enabled, setEnabled] = useState(!!initial.enabled);
  const [mode] = useState("person"); // AI-only on mobile — Wall mode
  // requires a green wall which ~99% of mobile users don't have. Advanced
  // users can access chroma-key via the gear-icon (StudioFxModal).
  const [bgMode, setBgMode] = useState(initial.bgMode || "none");    // none | blur | image | video
  const [bgUrl, setBgUrl] = useState(initial.bgUrl || "");
  const [bgDataUrl, setBgDataUrl] = useState(initial.bgDataUrl || "");
  // Blur intensity — 3 presets shown as separate chips (light / medium /
  // heavy). Legacy prefs without a level fall back to medium.
  const [blurLevel, setBlurLevel] = useState(initial.blurLevel || "medium");
  // Smart Frame — auto-centers the host on viewer screens using the
  // segmentation mask's bounding box (Zoom Pro-style steady-cam). The
  // host can leave it on, move freely around the room, and viewers
  // always see them centred.
  const [smartFrame, setSmartFrame] = useState(!!initial.smartFrame);
  // Touch-Up / Beauty filter — soft blur + brightness bump on the
  // segmented subject. Persisted in prefs so it survives sessions.
  const [touchUp, setTouchUp] = useState(!!initial.touchUp);
  // Auto Low-Light Boost — default ON (only engages in dark rooms).
  const [lowLight, setLowLight] = useState(initial.lowLight !== false);
  // Rim Light + Drop Shadow edge FX — grounds the cutout in virtual
  // backgrounds. Off by default.
  const [edgeFx, setEdgeFx] = useState(!!initial.edgeFx);
  // Manual framing override — "auto" is Smart Frame's heuristic,
  // "tight" locks a shoulders-up crop, "wide" disables zoom entirely.
  const [frameMode, setFrameMode] = useState(initial.frameMode || "auto");
  const [library, setLibrary] = useState(null); // null = not fetched
  const [myPins, setMyPins] = useState([]); // creator's pinned bg IDs (server-sourced)
  const [recents, setRecents] = useState(readRecents());
  const [generating, setGenerating] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiOpen, setAiOpen] = useState(false);

  // Background Memory — when GS is turned ON and the user has NO bg
  // currently selected (bgMode === "none"), restore the last bg they
  // explicitly applied so they don't have to re-pick every session.
  const restoredOnceRef = useRef(false);
  useEffect(() => {
    if (!enabled || restoredOnceRef.current) return;
    if (bgMode !== "none") { restoredOnceRef.current = true; return; }
    const last = initial.lastAppliedBg;
    if (last?.bgMode && last?.bgMode !== "none") {
      setBgMode(last.bgMode);
      setBgUrl(last.bgUrl || "");
      setBgDataUrl(last.bgDataUrl || "");
      restoredOnceRef.current = true;
      toast.success("Restored your last background");
    }
  }, [enabled, bgMode, initial.lastAppliedBg]);

  // Persist on every change. Also stash the "last applied" non-none
  // background separately so we can offer Background Memory restore
  // even if the user later toggles GS off (which would otherwise
  // overwrite bgMode with "none" in their prefs).
  useEffect(() => {
    const lastAppliedBg = (bgMode !== "none" && bgMode !== "blur" && bgMode !== "dim")
      ? { bgMode, bgUrl, bgDataUrl }
      : (initial.lastAppliedBg || null);
    writePrefs({
      enabled,
      removalMode: mode,
      bgMode,
      bgUrl,
      bgDataUrl,
      blurLevel,
      smartFrame,
      touchUp,
      lowLight,
      edgeFx,
      frameMode,
      lastAppliedBg,
      chromaColor: initial.chromaColor || "#00FF00",
      similarity: initial.similarity ?? 0.55,
      smoothness: initial.smoothness ?? 0.15,
    });
  }, [enabled, mode, bgMode, bgUrl, bgDataUrl, blurLevel, smartFrame, touchUp, lowLight, edgeFx, frameMode, initial.chromaColor, initial.similarity, initial.smoothness, initial.lastAppliedBg]);

  // Lazy-load the admin background library on first open. Also
  // captures the creator's per-account pinned bg IDs so we can sort
  // them right after the Tap In brand-pack (featured) tiles.
  useEffect(() => {
    if (!enabled || library !== null) return undefined;
    let cancelled = false;
    api.get("/greenscreen/backgrounds")
      .then(({ data }) => {
        if (cancelled) return;
        setLibrary(data?.items || []);
        setMyPins(Array.isArray(data?.my_pins) ? data.my_pins : []);
      })
      .catch(() => { if (!cancelled) setLibrary([]); });
    return () => { cancelled = true; };
  }, [enabled, library]);

  // Toggle a per-creator pin on a background tile (long-press in UI).
  // Optimistic UI: flip the local set first, then reconcile with the
  // server response so the carousel re-orders instantly.
  const togglePin = useCallback(async (bgId) => {
    if (!bgId) return;
    setMyPins((cur) => cur.includes(bgId) ? cur.filter((x) => x !== bgId) : [bgId, ...cur].slice(0, 12));
    try {
      const { data } = await api.post(`/greenscreen/backgrounds/${bgId}/pin`);
      if (Array.isArray(data?.my_pins)) setMyPins(data.my_pins);
      try { navigator.vibrate?.(12); } catch { /* unsupported */ }
      toast.success(data?.pinned ? "Pinned to your kit" : "Removed from your kit");
    } catch { /* swallow — optimistic state already updated */ }
  }, []);

  // Drive the shared `useGreenScreen` compositor hook. The hook owns
  // the rAF loop, mask blending, and produces a MediaStreamTrack that
  // we bubble up to the broadcaster.
  const background = useMemo(
    () => ({ mode: bgMode, url: bgUrl, dataUrl: bgDataUrl, blurLevel }),
    [bgMode, bgUrl, bgDataUrl, blurLevel]
  );
  const chroma = useMemo(
    () => ({
      enabled: true,
      mode,                          // useGreenScreen routes "person" → MediaPipe
      color: initial.chromaColor || "#00FF00",
      similarity: initial.similarity ?? 0.55,
      smoothness: initial.smoothness ?? 0.15,
    }),
    [mode, initial.chromaColor, initial.similarity, initial.smoothness]
  );
  const { track, ready, bgFailed, segStatus, subjectCanvasRef, videoElRef } = useGreenScreen({
    stream: cameraStream,
    enabled,
    background,
    chroma,
    smartFrame,
    frameMode,
    touchUp,
    lowLight,
    edgeFx,
    captureFps,
  });

  // Surface background-load failures as a toast so the host understands
  // WHY they're seeing a placeholder instead of their picked scene
  // (CORS, 404, or decode error — usually server not sending CORS
  // headers on that image URL).
  useEffect(() => {
    if (bgFailed) toast.error("Background image couldn't load — pick another or try again");
  }, [bgFailed]);

  // Surface MediaPipe load failure — on the rare browsers where the
  // jsdelivr CDN is blocked, the AI cutout won't work. Encourage the
  // host to use BLUR L/M/H which don't need MediaPipe.
  useEffect(() => {
    if (segStatus === "failed") {
      toast.error("AI cutout couldn't start on this device — try BLUR instead");
    }
  }, [segStatus]);

  // Bubble the track up. When `enabled` flips off, useGreenScreen
  // returns null which restores the raw camera in the broadcaster.
  const onTrackRef = useRef(onCompositedTrack);
  useEffect(() => { onTrackRef.current = onCompositedTrack; }, [onCompositedTrack]);
  useEffect(() => { try { onTrackRef.current?.(track); } catch { /* parent unmounted */ } }, [track]);
  // Also clear the parent's gsTrack when THIS component unmounts (e.g.
  // rawStream briefly goes null on a camera reacquire). Without this,
  // the broadcaster keeps a reference to the stopped composite track
  // and viewers see a frozen frame until the next explicit track swap.
  useEffect(() => {
    return () => { try { onTrackRef.current?.(null); } catch { /* fine */ } };
  }, []);

  // --- Actions ------------------------------------------------------
  const applyItem = useCallback((it) => {
    if (!it) return;
    setBgMode(it.kind === "video" || it.mode === "video" ? "video" : "image");
    setBgUrl(it.url || "");
    setBgDataUrl(it.data_url || "");
    pushRecent(it);
    setRecents(readRecents());
    // Tactile feedback for the user — phones love this.
    try { navigator.vibrate?.(8); } catch { /* unsupported */ }
  }, []);

  const setBgNone = () => { setBgMode("none"); setBgUrl(""); setBgDataUrl(""); };
  const setBgDim = () => { setBgMode("dim"); setBgUrl(""); setBgDataUrl(""); };
  const setBgBlur = (level) => {
    setBgMode("blur");
    setBgUrl("");
    setBgDataUrl("");
    if (level) setBlurLevel(level);
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so the same file can be re-picked later
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Background must be under 8 MB.");
      return;
    }
    // Local data URL preview — non-admins can't write to the shared
    // admin library, so we use a per-session local copy. Admins use
    // the StudioFxModal's full UI to publish to the library.
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      setBgMode(file.type.startsWith("video/") ? "video" : "image");
      setBgDataUrl(dataUrl);
      setBgUrl("");
      try { navigator.vibrate?.(8); } catch { /* unsupported */ }
      toast.success("Custom background applied.");
    };
    reader.readAsDataURL(file);
  };

  const onGenerate = async () => {
    const prompt = aiPrompt.trim();
    if (!prompt) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/greenscreen/backgrounds/generate", { prompt });
      if (data?.item) {
        setLibrary((cur) => [data.item, ...(cur || [])]);
        applyItem(data.item);
        setAiPrompt("");
        setAiOpen(false);
        toast.success("Background generated.");
      }
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't generate background.");
    } finally {
      setGenerating(false);
    }
  };

  // Merged carousel = featured (admin brand pack) + creator's pins +
  // recents + rest of library, deduped, capped at 24 tiles for speed.
  const carousel = useMemo(() => {
    const lib = library || [];
    const libById = new Map(lib.map((x) => [x.id, x]));
    const seen = new Set();
    const out = [];
    // 1) featured (admin brand pack) — these are already first in `lib`
    //    via the server sort, but we re-walk explicitly so we own the
    //    interleave with pins below.
    for (const l of lib) {
      if (l.featured && !seen.has(l.id)) { seen.add(l.id); out.push(l); }
    }
    // 2) my pins (creator brand kit)
    for (const id of myPins) {
      const it = libById.get(id);
      if (it && !seen.has(it.id)) { seen.add(it.id); out.push(it); }
    }
    // 3) recents
    for (const r of recents) {
      if (r?.id && !seen.has(r.id)) {
        // Prefer the live library copy if we have it (richer fields).
        const it = libById.get(r.id) || r;
        seen.add(it.id);
        out.push(it);
      }
    }
    // 4) everything else
    for (const l of lib) {
      if (!seen.has(l.id)) { seen.add(l.id); out.push(l); }
    }
    return out.slice(0, 24);
  }, [recents, library, myPins]);

  const activeMatchesNone = bgMode === "none";
  const activeMatchesDim = bgMode === "dim";
  const activeMatchesBlur = bgMode === "blur";
  const activeMatchesBlurLight = activeMatchesBlur && blurLevel === "light";
  const activeMatchesBlurMed = activeMatchesBlur && blurLevel === "medium";
  const activeMatchesBlurHeavy = activeMatchesBlur && blurLevel === "heavy";
  const activeBgKey = bgUrl || bgDataUrl || "";

  return (
    <div
      data-testid="mobile-gs-bar"
      className="bg-black/80 backdrop-blur-xl border-t border-white/10 px-3 pt-2 pb-[max(env(safe-area-inset-bottom),0.5rem)] text-white [@media(max-height:700px)]:pt-1 [@media(max-height:700px)]:pb-[max(env(safe-area-inset-bottom),0.25rem)]"
    >
      {/* Row 1: Header — ON/OFF + scrollable FX chip strip + gear.
          The chip strip scrolls HORIZONTALLY (flex-1 min-w-0) so the
          8 toggles never overflow off-screen on narrow phones — this
          was the "view is obstructed on smaller iPhones" bug. */}
      <div className="flex items-center gap-2 mb-2 [@media(max-height:700px)]:mb-1">
        <button
          type="button"
          onClick={() => setEnabled((v) => !v)}
          aria-pressed={enabled}
          data-testid="mobile-gs-toggle"
          title={enabled ? "AI is removing your background — tap to turn off" : "AI removes your background — no green wall needed"}
          className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-black uppercase tracking-wider border ${
            enabled
              ? "bg-tv-accent text-black border-tv-accent shadow-[0_6px_18px_rgba(245,196,69,0.35)]"
              : "bg-white/5 text-white/70 border-white/15"
          }`}
        >
          <Sparkles className="w-3 h-3" />
          {enabled ? "AI · ON" : "AI Background"}
          {enabled && (
            <span
              data-testid="mobile-gs-ready"
              className={`ml-1 w-1.5 h-1.5 rounded-full ${ready ? "bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.9)]" : "bg-white/40 animate-pulse"}`}
              title={ready ? "AI pipeline ready — cutting out your background" : "AI booting up… (~1 s)"}
            />
          )}
        </button>

        {enabled && (
          <div
            className="flex items-center gap-1 flex-1 min-w-0 overflow-x-auto no-scrollbar"
            style={{ WebkitOverflowScrolling: "touch" }}
            role="tablist"
            aria-label="Framing"
          >
            {/* Smart Frame — auto-centers the host using the segmentation
                mask's bbox. Powers the Zoom-style "no matter which way
                the camera faces" experience — flip the camera and the
                subject stays centred without touching a button. */}
            <button
              type="button"
              onClick={() => {
                setSmartFrame((v) => !v);
                try { navigator.vibrate?.(8); } catch { /* unsupported */ }
              }}
              aria-pressed={smartFrame}
              data-testid="mobile-gs-smart-frame"
              className={`shrink-0 whitespace-nowrap text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-full border inline-flex items-center gap-1 ${
                smartFrame
                  ? "bg-tv-accent text-black border-tv-accent shadow-[0_4px_12px_rgba(245,196,69,0.35)]"
                  : "bg-white/5 text-white/60 border-white/15"
              }`}
              title="Smart Frame — auto-centers you regardless of camera direction"
            >
              <Maximize2 className="w-3 h-3" /> Auto Frame
            </button>
            {/* Touch-Up / Beauty filter — soft skin + brightness bump.
                Zoom / TikTok Live have this as a one-tap toggle; it's
                the #1 requested creator polish tool. */}
            <button
              type="button"
              onClick={() => {
                setTouchUp((v) => !v);
                try { navigator.vibrate?.(8); } catch { /* unsupported */ }
              }}
              aria-pressed={touchUp}
              data-testid="mobile-gs-touch-up"
              className={`shrink-0 whitespace-nowrap text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-full border inline-flex items-center gap-1 ${
                touchUp
                  ? "bg-tv-accent text-black border-tv-accent shadow-[0_4px_12px_rgba(245,196,69,0.35)]"
                  : "bg-white/5 text-white/60 border-white/15"
              }`}
              title="Touch-Up — soft skin + brightness (Zoom / TikTok-style beauty filter)"
            >
              <Wand2 className="w-3 h-3" /> Touch Up
            </button>
            {/* Auto Low-Light Boost — auto-lifts exposure in dark rooms. */}
            <button
              type="button"
              onClick={() => {
                setLowLight((v) => !v);
                try { navigator.vibrate?.(8); } catch { /* unsupported */ }
              }}
              aria-pressed={lowLight}
              data-testid="mobile-gs-low-light"
              className={`shrink-0 whitespace-nowrap text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-full border inline-flex items-center gap-1 ${
                lowLight
                  ? "bg-tv-accent text-black border-tv-accent shadow-[0_4px_12px_rgba(245,196,69,0.35)]"
                  : "bg-white/5 text-white/60 border-white/15"
              }`}
              title="Auto Low-Light Boost — brightens you automatically in dark rooms"
            >
              <Sun className="w-3 h-3" /> Low Light
            </button>
            {/* Rim Light + Drop Shadow — grounds the cutout in virtual sets. */}
            <button
              type="button"
              onClick={() => {
                setEdgeFx((v) => !v);
                try { navigator.vibrate?.(8); } catch { /* unsupported */ }
              }}
              aria-pressed={edgeFx}
              data-testid="mobile-gs-rim-light"
              className={`shrink-0 whitespace-nowrap text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-full border inline-flex items-center gap-1 ${
                edgeFx
                  ? "bg-tv-accent text-black border-tv-accent shadow-[0_4px_12px_rgba(245,196,69,0.35)]"
                  : "bg-white/5 text-white/60 border-white/15"
              }`}
              title="Rim Light — edge glow + soft shadow so you look anchored in the scene"
            >
              <Lightbulb className="w-3 h-3" /> Rim Light
            </button>
            {/* Manual framing — TIGHT (shoulders-up) / WIDE (whole scene).
                Bypasses Smart Frame's heuristic so creators can lock the
                exact vibe (podcast tight vs full-body dance wide). */}
            {["auto", "tight", "wide"].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setFrameMode(m)}
                aria-pressed={frameMode === m}
                data-testid={`mobile-gs-frame-${m}`}
                className={`shrink-0 whitespace-nowrap text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-full border inline-flex items-center gap-1 ${
                  frameMode === m
                    ? "bg-tv-accent text-black border-tv-accent shadow-[0_4px_12px_rgba(245,196,69,0.35)]"
                    : "bg-white/5 text-white/60 border-white/15"
                }`}
                title={m === "auto" ? "Let AI pick your framing" : m === "tight" ? "Zoom in shoulders-up (podcast vibe)" : "Whole scene visible (wide shot)"}
              >
                {m === "auto" ? "Auto" : m === "tight" ? "Tight" : "Wide"}
              </button>
            ))}
          </div>
        )}

        {/* Spacer only when GS is off (when on, the chip strip flexes). */}
        {!enabled && <div className="flex-1 min-w-0" />}

        {enabled && (
          <>
            <label
              data-testid="mobile-gs-upload-trigger"
              className="shrink-0 w-9 h-9 rounded-full bg-white/5 border border-white/15 hover:bg-white/10 flex items-center justify-center cursor-pointer"
              title="Upload your own background"
            >
              <Upload className="w-3.5 h-3.5" />
              <input type="file" accept="image/*,video/*" className="hidden" onChange={onUpload} />
            </label>
            <button
              type="button"
              onClick={() => setAiOpen((v) => !v)}
              data-testid="mobile-gs-ai-trigger"
              className={`shrink-0 w-9 h-9 rounded-full border flex items-center justify-center ${
                aiOpen ? "bg-tv-accent text-black border-tv-accent" : "bg-white/5 text-white border-white/15 hover:bg-white/10"
              }`}
              title="Generate a background with AI"
            >
              <Wand2 className="w-3.5 h-3.5" />
            </button>
            {onOpenAdvanced && (
              <button
                type="button"
                onClick={onOpenAdvanced}
                data-testid="mobile-gs-gear"
                className="shrink-0 w-9 h-9 rounded-full bg-white/5 border border-white/15 hover:bg-white/10 flex items-center justify-center"
                title="Advanced settings"
              >
                <Settings className="w-3.5 h-3.5" />
              </button>
            )}
          </>
        )}
      </div>

      {/* Row 2: Carousel — only when GS is on. Horizontal scroll. */}
      {enabled && (
        <>
          <div
            data-testid="mobile-gs-carousel"
            className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1 snap-x snap-mandatory scroll-pl-2"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {/* NONE chip */}
            <CarouselChip
              testid="mobile-gs-chip-none"
              active={activeMatchesNone}
              onClick={setBgNone}
              kind="none"
              subjectCanvasRef={subjectCanvasRef}
              videoElRef={videoElRef}
            />
            {/* DIM chip — darkened room, no blur. The podcast/stage-light
                look: reads far cleaner on camera than blur. */}
            <CarouselChip
              testid="mobile-gs-chip-dim"
              active={activeMatchesDim}
              onClick={setBgDim}
              kind="dim"
              subjectCanvasRef={subjectCanvasRef}
              videoElRef={videoElRef}
            />
            {/* BLUR chips — 3 intensity levels so creators can pick the
                exact vibe (Zoom-subtle → cinematic bokeh) instead of a
                single one-size-fits-all blur. */}
            <CarouselChip
              testid="mobile-gs-chip-blur-light"
              active={activeMatchesBlurLight}
              onClick={() => setBgBlur("light")}
              kind="blur"
              blurLevel="light"
              subjectCanvasRef={subjectCanvasRef}
              videoElRef={videoElRef}
            />
            <CarouselChip
              testid="mobile-gs-chip-blur-medium"
              active={activeMatchesBlurMed}
              onClick={() => setBgBlur("medium")}
              kind="blur"
              blurLevel="medium"
              subjectCanvasRef={subjectCanvasRef}
              videoElRef={videoElRef}
            />
            <CarouselChip
              testid="mobile-gs-chip-blur-heavy"
              active={activeMatchesBlurHeavy}
              onClick={() => setBgBlur("heavy")}
              kind="blur"
              blurLevel="heavy"
              subjectCanvasRef={subjectCanvasRef}
              videoElRef={videoElRef}
            />
            {/* Library + pins + recents (already merged + sorted above) */}
            {carousel.map((it) => (
              <CarouselChip
                key={it.id}
                testid={`mobile-gs-chip-${it.id}`}
                active={!!activeBgKey && (it.url === activeBgKey || it.data_url === activeBgKey)}
                onClick={() => applyItem(it)}
                onLongPress={() => togglePin(it.id)}
                kind="image"
                src={it.url || it.data_url}
                label={it.title}
                featured={!!it.featured}
                pinned={myPins.includes(it.id)}
                subjectCanvasRef={subjectCanvasRef}
                videoElRef={videoElRef}
              />
            ))}
            {/* Empty-state hint */}
            {library !== null && carousel.length === 0 && (
              <div className="text-[10px] text-white/40 italic px-2 py-3 shrink-0">
                No backgrounds yet — tap <ImagePlus className="inline w-3 h-3 -mt-0.5" /> or <Wand2 className="inline w-3 h-3 -mt-0.5" /> to add one.
              </div>
            )}
            {library === null && (
              <div className="text-[10px] text-white/40 italic px-2 py-3 shrink-0 inline-flex items-center gap-1">
                <RefreshCw className="w-3 h-3 animate-spin" /> Loading…
              </div>
            )}
          </div>

          {/* Row 3 (conditional): AI prompt input */}
          {aiOpen && (
            <div data-testid="mobile-gs-ai-row" className="mt-2 flex items-center gap-2">
              <input
                type="text"
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") onGenerate(); }}
                placeholder="Describe a background — e.g. ‘sunset rooftop, neon city’"
                data-testid="mobile-gs-ai-prompt"
                className="flex-1 min-w-0 bg-white/5 border border-white/15 rounded-full px-3 py-1.5 text-xs outline-none focus:border-tv-accent placeholder:text-white/35"
              />
              <button
                type="button"
                onClick={onGenerate}
                disabled={generating || !aiPrompt.trim()}
                data-testid="mobile-gs-ai-gen"
                className={`shrink-0 inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[10px] font-black uppercase tracking-wider border ${
                  generating || !aiPrompt.trim()
                    ? "bg-white/5 text-white/40 border-white/10 cursor-not-allowed"
                    : "bg-tv-accent text-black border-tv-accent"
                }`}
              >
                {generating ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                Gen
              </button>
              <button
                type="button"
                onClick={() => { setAiOpen(false); setAiPrompt(""); }}
                data-testid="mobile-gs-ai-close"
                aria-label="Close AI prompt"
                className="shrink-0 w-7 h-7 rounded-full bg-white/5 border border-white/15 flex items-center justify-center"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )}

          {/* Row 4 (conditional): AI-only on mobile — no Wall hint needed
              since chroma-key is not exposed here. Advanced users can
              still get to Wall mode via the gear icon → StudioFxModal. */}
        </>
      )}
    </div>
  );
}

function CarouselChip({ testid, active, onClick, onLongPress, kind, src, label, featured = false, pinned = false, blurLevel, subjectCanvasRef, videoElRef }) {
  // Long-press detector — 600ms hold without movement → onLongPress.
  // Triggers per-creator pin/unpin in the carousel without adding an
  // extra always-visible button on every tile.
  const pressTimerRef = useRef(0);
  const longFiredRef = useRef(false);
  const startPress = () => {
    longFiredRef.current = false;
    pressTimerRef.current = window.setTimeout(() => {
      longFiredRef.current = true;
      onLongPress?.();
    }, 600);
  };
  const endPress = () => {
    if (pressTimerRef.current) { clearTimeout(pressTimerRef.current); pressTimerRef.current = 0; }
  };
  const cancelPress = () => { endPress(); longFiredRef.current = false; };
  const handleClick = () => { if (!longFiredRef.current) onClick?.(); };
  const ring = active ? "ring-2 ring-tv-accent" : "ring-1 ring-white/10";
  const base = "snap-start shrink-0 w-14 h-14 [@media(max-height:700px)]:w-11 [@media(max-height:700px)]:h-11 rounded-xl overflow-hidden relative bg-white/5 flex items-center justify-center";
  if (kind === "image" && src) {
    return (
      <button
        type="button"
        onClick={handleClick}
        onMouseDown={onLongPress ? startPress : undefined}
        onMouseUp={onLongPress ? endPress : undefined}
        onMouseLeave={onLongPress ? cancelPress : undefined}
        onTouchStart={onLongPress ? startPress : undefined}
        onTouchEnd={onLongPress ? endPress : undefined}
        onContextMenu={(e) => e.preventDefault()}
        data-testid={testid}
        aria-pressed={active}
        title={label || "Apply background"}
        className={`${base} ${ring}`}
      >
        {/* Live composite preview — draws the segmented host over
            this specific background so the chip becomes a "prevu"
            of what the viewer will see. Falls back to a static
            <img> when segmentation isn't running yet. */}
        <ChipLivePreview
          subjectCanvasRef={subjectCanvasRef}
          videoElRef={videoElRef}
          bgSrc={src}
          bgKind="image"
          fallbackSrc={src}
        />
        {active && <div className="absolute inset-0 bg-tv-accent/15 pointer-events-none" />}
        {featured && (
          <div className="absolute top-0.5 right-0.5 bg-tv-accent text-black rounded-full w-4 h-4 flex items-center justify-center shadow" title="Tap In branded background">
            <Star className="w-2.5 h-2.5 fill-current" />
          </div>
        )}
        {pinned && !featured && (
          <div className="absolute top-0.5 right-0.5 bg-white text-black rounded-full w-4 h-4 flex items-center justify-center shadow" title="Your pinned background">
            <Pin className="w-2.5 h-2.5 fill-current" />
          </div>
        )}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      className={`${base} ${ring} ${active ? "bg-tv-accent/20" : ""}`}
      title={
        kind === "none"
          ? "Show your room (no background)"
          : kind === "dim"
            ? "Dim — darkened room, no blur (clean podcast look)"
            : blurLevel === "light"
              ? "Light blur — subtle Zoom vibe"
              : blurLevel === "heavy"
                ? "Heavy blur — cinematic bokeh"
                : "Medium blur (default)"
      }
    >
      {/* NONE / BLUR chips get a live preview of the raw camera
          (NONE) or a blurred version of the camera (BLUR) so the
          host can compare all three-plus tiles at a glance. */}
      <ChipLivePreview
        subjectCanvasRef={subjectCanvasRef}
        videoElRef={videoElRef}
        bgSrc={null}
        bgKind={kind}
        blurLevel={blurLevel}
      />
      <span className="absolute inset-x-0 bottom-0 text-[8px] font-black uppercase tracking-widest text-white/90 bg-black/60 py-0.5">
        {kind === "none" ? "Off" : kind === "dim" ? "Dim" : blurLevel === "light" ? "Blur L" : blurLevel === "heavy" ? "Blur H" : "Blur M"}
      </span>
    </button>
  );
}

/**
 * ChipLivePreview — a tiny always-mounted <canvas> that draws a
 * chip-sized composite (background + segmented host) at ~12 fps.
 *
 * Reuses the shared subject canvas from the running `useGreenScreen`
 * pipeline so N chips cost only one segmentation loop. When the
 * subject canvas is not yet populated (first ~200 ms of a session
 * or GS turned off) we render the static bg image as a fallback so
 * the carousel is never blank.
 */
function ChipLivePreview({ subjectCanvasRef, videoElRef, bgSrc, bgKind, blurLevel, fallbackSrc }) {
  const canvasRef = useRef(null);
  const bgImgRef = useRef(null);
  const rafRef = useRef(0);
  const lastFrameTsRef = useRef(0);

  // Pre-load the background image once (or when it changes).
  useEffect(() => {
    if (bgKind !== "image" || !bgSrc) { bgImgRef.current = null; return undefined; }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.decoding = "async";
    let cancelled = false;
    img.onload = () => { if (!cancelled) bgImgRef.current = img; };
    img.onerror = () => { if (!cancelled) bgImgRef.current = null; };
    img.src = bgSrc;
    return () => { cancelled = true; bgImgRef.current = null; };
  }, [bgSrc, bgKind]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    // Chips are ~56×56 CSS px; render at 2× DPI for crispness.
    const W = 112;
    const H = 112;
    canvas.width = W;
    canvas.height = H;
    ctx.imageSmoothingEnabled = true;
    try { ctx.imageSmoothingQuality = "high"; } catch { /* older browser */ }

    const tick = (ts) => {
      // Throttle to ~12 fps — plenty for a 56 px chip and cheap.
      if (ts - lastFrameTsRef.current > 80) {
        lastFrameTsRef.current = ts;
        const subject = subjectCanvasRef?.current || null;
        const vid = videoElRef?.current || null;

        // Draw background layer
        if (bgKind === "image" && bgImgRef.current) {
          drawCover(ctx, bgImgRef.current, W, H);
        } else if (bgKind === "blur" && vid && vid.readyState >= 2) {
          // Chip-scale blur — light/medium/heavy proportionally scaled
          // down from the compositor's 10/20/40 px so a 112×112 chip
          // reads the same relative intensity as the full-screen preview.
          const chipBlur = blurLevel === "light" ? 3 : blurLevel === "heavy" ? 12 : 6;
          const chipBright = blurLevel === "light" ? 0.9 : blurLevel === "heavy" ? 0.5 : 0.7;
          ctx.filter = `blur(${chipBlur}px) brightness(${chipBright})`;
          drawCover(ctx, vid, W, H);
          ctx.filter = "none";
        } else if (bgKind === "dim" && vid && vid.readyState >= 2) {
          ctx.filter = "brightness(0.4) saturate(0.75)";
          drawCover(ctx, vid, W, H);
          ctx.filter = "none";
        } else if (bgKind === "none" && vid && vid.readyState >= 2) {
          drawCover(ctx, vid, W, H);
        } else {
          // Empty background — clear so we don't retain stale pixels
          // between chip re-mounts.
          ctx.clearRect(0, 0, W, H);
          // Only when we have absolutely nothing (no subject, no bg,
          // no camera yet), paint the fallback static bg image so the
          // chip isn't blank before the pipeline warms up.
          if (bgKind === "image" && fallbackSrc && !subject) {
            // Nothing to do here — fallback shown by <img> layer below.
          }
        }

        // Draw the segmented host on top (only when GS pipeline is up).
        if (subject && subject.width > 0) {
          // Composite the person cutout at chip scale — the subject
          // canvas has transparent bg + masked foreground already.
          try { ctx.drawImage(subject, 0, 0, W, H); } catch { /* canvas may be torn down mid-frame */ }
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { try { cancelAnimationFrame(rafRef.current); } catch { /* effect torn down */ } };
  }, [subjectCanvasRef, videoElRef, bgKind, blurLevel, fallbackSrc]);

  return (
    <>
      {/* Fallback static image behind the canvas — visible during the
          first frames before the compositor produces anything and if
          the browser blocks canvas rendering (rare). */}
      {bgKind === "image" && fallbackSrc && (
        <img
          src={fallbackSrc}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
          aria-hidden="true"
        />
      )}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        aria-hidden="true"
      />
    </>
  );
}
